import { ArrowRight, Sparkles } from 'lucide-react';
import { resolveInsightType, type Insight, type InsightType } from '../utils/insightTypes';

/**
 * FlaggedInsights — the "Dixie flagged" block inside a chapter card (#601).
 *
 * A single presentational sub-block: it renders a typed list of AI-generated
 * findings for one chapter. No data fetching, no local state. It returns null
 * when there are no insights, so an insight-free chapter shows no empty box.
 *
 * The panel is a nested cream surface on the white card — the only place in the
 * app where cream is used as a fill. That is deliberate: it marks
 * machine-generated content as distinct from the recorded facts around it. The
 * type contract (colors, labels, icons, header-dot colors) lives in
 * ../utils/insightTypes so the chapter-header dot row and future digest views
 * read the same source rather than re-declaring hex values.
 */

export type { Insight, InsightType } from '../utils/insightTypes';

export interface FlaggedInsightsProps {
  insights: Insight[];
  className?: string;
}

function TypePill({ type }: { type: InsightType }) {
  const spec = resolveInsightType(type);
  const { Icon } = spec;
  return (
    <span
      className="inline-flex items-center whitespace-nowrap"
      style={{
        gap: 6,
        borderRadius: 9999,
        padding: '3px 9px',
        fontSize: 10.5,
        fontWeight: 600,
        flexShrink: 0,
        color: spec.text,
        backgroundColor: spec.fill,
        border: `1px solid ${spec.border}`,
      }}
    >
      <Icon size={11} aria-hidden />
      {spec.label}
    </span>
  );
}

function InsightRow({ insight }: { insight: Insight }) {
  return (
    <li
      className="insight-row"
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        padding: '10px 8px',
        margin: '0 -8px',
        borderRadius: 9,
        borderTop: '1px solid #f1ece1',
      }}
    >
      <TypePill type={insight.type} />
      {/* min-width:0 lets long titles wrap instead of forcing the row wide. */}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#06143b', lineHeight: 1.4, textWrap: 'pretty' }}>
          {insight.title}
        </div>
        <div style={{ fontSize: 12.5, fontWeight: 400, color: '#667491', lineHeight: 1.55, marginTop: 2, textWrap: 'pretty' }}>
          {insight.body}
        </div>
        {insight.cta && (
          <a
            className="insight-cta"
            href={insight.cta.href}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              marginTop: 6,
              fontSize: 11.5,
              fontWeight: 600,
              color: '#ea580c',
            }}
          >
            {insight.cta.label}
            <ArrowRight size={12} aria-hidden />
          </a>
        )}
      </div>
    </li>
  );
}

export function FlaggedInsights({ insights, className }: FlaggedInsightsProps) {
  if (!insights || insights.length === 0) return null;

  return (
    <section
      aria-label="Dixie flagged insights"
      className={className}
      style={{
        backgroundColor: '#fbfaf6',
        border: '1px solid #efe9dd',
        borderRadius: 12,
        padding: '14px 16px',
        margin: '2px 0 14px',
      }}
    >
      <style>{`
        .insight-row { transition: background 120ms; }
        .insight-row:hover { background: #f6f2e8; }
        .insight-cta:hover { text-decoration: underline; }
      `}</style>
      <div style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 6 }}>
        <Sparkles size={12} strokeWidth={2} aria-hidden style={{ color: '#ea580c' }} />
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            color: '#ea580c',
          }}
        >
          Dixie flagged
        </span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 400, color: '#c9a06a' }}>
          {insights.length}
        </span>
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {insights.map((insight) => (
          <InsightRow key={insight.id} insight={insight} />
        ))}
      </ul>
    </section>
  );
}
